import React, { useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import Utilities from '@util/Utilities';
import { NO_VALUE, ValueFormat, formatSigned, formatStayDateLong, formatValue } from './chartFormat';
import { CHART_HEIGHT, ChartRole, ChartSize, colorOfRole } from './chartTokens';

// A table of exact values that the user can sort: the rows behind a chart, or a list such as
// "the 10 dates with the largest difference to the compset".
// The look is the documented table pattern (Patterns/Table): sticky header, xs text, zebra rows.
// Each column has a format: text, date (Mon 7 Dec 2026) or a chart format (money, percent, number),
// so a table shows a value exactly as the chart next to it. Numbers align right.
// A click on a column header sorts by that column; a second click turns the order. A missing value
// is '–' and always goes last.
// A column can carry a chart role: then a short line in the colour of that role keys the column to
// its line in the chart. The 'series' columns take the series slots in their order, as in the chart.
// With `size`, the table scrolls inside the height of a chart of that size (for the table view of a
// ChartCard). On a narrow screen the table scrolls sideways inside its frame; the first column stays. The table knows nothing about the data: the caller gives the columns and the rows.
// Column groups put a header row above the columns ("Today", "Situation D-7"); a thin line separates the groups.
// A row with children opens and closes with the chevron in its first cell (a group and its segments); the children
// follow their row, indented on a gray-50 ground, and keep the order of the caller. Sorting moves only the top rows.
// A muted row (gray text) is a remainder such as "No segment". A footer row (the total) stays last, in bold.
// A column with the tone 'change' shows a positive number in green-700, a negative one in red-700 and 0 in gray-400.

export type DataTableFormat = 'text' | 'date' | ValueFormat;

export type DataTableColumn = {
	id: string,
	label: string,
	// Default 'text'.
	format?: DataTableFormat,
	// An ISO 4217 code for 'money'. Default 'EUR'.
	currency?: string,
	// Default 0.
	decimals?: number,
	// Show + before a positive number (a difference).
	signed?: boolean,
	// The chart role of the column: a key line in the header.
	role?: ChartRole,
	// Default true.
	sortable?: boolean,
	// 'change': green for a positive number, red for a negative one, gray for 0 (a pickup).
	tone?: 'change',
};

export type DataTableColumnGroup = {
	// Empty text for the columns above which no group label stands (the first column).
	label: string,
	// The number of columns of the group.
	span: number,
};

export type DataTableCell = string | number | null;

export type DataTableRow = {
	id: string,
	cells: Record<string, DataTableCell>,
	// Rows that this row opens and closes (the segments of a group).
	children?: DataTableRow[],
	// A short gray text after the first cell, for example "· 2".
	note?: string,
	// Gray text: a remainder row such as "No segment".
	muted?: boolean,
};

export type DataTableSort = {
	columnId: string,
	direction: 'asc' | 'desc',
};

type Props = {
	columns: DataTableColumn[],
	rows: DataTableRow[],
	// The name of the table for a screen reader.
	ariaLabel: string,
	// The order at the start. Without it, the rows keep the order of the caller.
	defaultSort?: DataTableSort,
	// The table scrolls inside the height of a chart of this size: small 200 px, normal 280 px, large 360 px.
	size?: ChartSize,
	// Default true: a border and a shadow. false inside a card (ChartCard).
	framed?: boolean,
	// The text when there is no row.
	emptyLabel?: string,
	// A header row above the columns. The spans add up to the number of columns.
	columnGroups?: DataTableColumnGroup[],
	// A last row in bold (the total). It does not move when the user sorts.
	footer?: DataTableRow,
	// The ids of the rows with children that are open at the start.
	defaultExpanded?: string[],
};

const isNumberFormat = (format: DataTableFormat): boolean => format === 'money' || format === 'percent' || format === 'number';

const cellText = (value: DataTableCell, column: DataTableColumn): string => {
	if (value === null || value === undefined || value === '') return NO_VALUE;
	const format = column.format ?? 'text';
	if (format === 'text') return String(value);
	if (format === 'date') return typeof value === 'string' ? formatStayDateLong(value) : String(value);
	const n = typeof value === 'number' ? value : Number(value);
	const options = { currency: column.currency, decimals: column.decimals };
	return column.signed === true ? formatSigned(n, format, options) : formatValue(n, format, options);
};

const toneClass = (value: DataTableCell, column: DataTableColumn): string => {
	if (column.tone !== 'change' || typeof value !== 'number') return '';
	if (value > 0) return 'font-medium text-green-700';
	if (value < 0) return 'font-medium text-red-700';
	return 'text-gray-400';
};

const compare = (a: DataTableCell, b: DataTableCell): number => {
	if (typeof a === 'number' && typeof b === 'number') return a - b;
	return String(a).localeCompare(String(b));
};

const DataTable: React.FunctionComponent<Props> = ({
	columns, rows, ariaLabel, defaultSort, size, framed = true, emptyLabel = 'No data for this period.',
	columnGroups, footer, defaultExpanded = [],
}) => {
	const [sort, setSort] = useState<DataTableSort | undefined>(defaultSort);
	const [expanded, setExpanded] = useState<Set<string>>(() => new Set(defaultExpanded));
	const toggle = (id: string) => setExpanded(current => {
		const next = new Set(current);
		if (next.has(id)) next.delete(id); else next.add(id);
		return next;
	});
	// The first column of each group after the first: a thin line on its left side.
	const groupStarts = useMemo(() => {
		const starts = new Set<number>();
		let index = 0;
		(columnGroups ?? []).forEach((group, g) => {
			if (g > 0) starts.add(index);
			index += group.span;
		});
		return starts;
	}, [columnGroups]);

	const sorted = useMemo(() => {
		if (sort === undefined) return rows;
		const factor = sort.direction === 'asc' ? 1 : -1;
		return [...rows].sort((r1, r2) => {
			const a = r1.cells[sort.columnId];
			const b = r2.cells[sort.columnId];
			const aMissing = a === null || a === undefined || a === '';
			const bMissing = b === null || b === undefined || b === '';
			if (aMissing || bMissing) return aMissing === bMissing ? 0 : (aMissing ? 1 : -1);
			return compare(a, b) * factor;
		});
	}, [rows, sort]);

	const keyColors = useMemo(() => {
		let seriesIndex = 0;
		return columns.map(c => {
			if (c.role === undefined) return undefined;
			const color = colorOfRole(c.role, seriesIndex);
			if (c.role === 'series') seriesIndex += 1;
			return color;
		});
	}, [columns]);

	const onSort = (column: DataTableColumn) => {
		setSort(current => current !== undefined && current.columnId === column.id
			? { columnId: column.id, direction: current.direction === 'asc' ? 'desc' : 'asc' }
			: { columnId: column.id, direction: isNumberFormat(column.format ?? 'text') ? 'desc' : 'asc' });
	};

	const renderRow = (row: DataTableRow, kind: 'top' | 'child' | 'footer', hasChildren: boolean, open: boolean) =>
		<tr key={`${kind}-${row.id}`} className={Utilities.classNames(kind === 'top' ? 'bg-white' : 'bg-gray-50', kind === 'footer' ? 'border-t border-gray-300' : '')}>
			{columns.map((column, i) => {
				const value = row.cells[column.id];
				const first = i === 0;
				return <td key={column.id}
					className={Utilities.classNames(
						'whitespace-nowrap py-2.5 text-xs',
						first ? Utilities.classNames('sticky left-0 bg-inherit pr-3 font-medium', kind === 'child' ? 'pl-10 sm:pl-12' : 'pl-4 sm:pl-6') : 'px-3',
						kind === 'footer' ? 'font-semibold text-gray-900'
							: (first ? (row.muted === true || kind === 'child' ? 'text-gray-500' : 'text-gray-900') : 'text-gray-500'),
						isNumberFormat(column.format ?? 'text') ? 'text-right tabular-nums' : 'text-left',
						groupStarts.has(i) ? 'border-l border-gray-200' : '',
						kind !== 'footer' && !first ? toneClass(value, column) : '',
					)}>
					{first && hasChildren
						? <button type="button" aria-expanded={open} onClick={() => toggle(row.id)}
							className="-ml-1 inline-flex items-center gap-x-1 rounded focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
							{open
								? <ChevronDownIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-500" />
								: <ChevronRightIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-500" />}
							<span>{cellText(value, column)}</span>
							{row.note !== undefined && <span className="font-normal text-gray-400">{row.note}</span>}
						</button>
						: <>
							{cellText(value, column)}
							{first && row.note !== undefined && <span className="ml-1 font-normal text-gray-400">{row.note}</span>}
						</>}
				</td>;
			})}
		</tr>;

	return <div className={Utilities.classNames(framed ? 'rounded-lg border border-gray-200 bg-white shadow-sm' : '', 'overflow-hidden')}>
		<div className="overflow-auto" style={size === undefined ? undefined : { maxHeight: CHART_HEIGHT[size] }}>
			<table className="min-w-full divide-y divide-gray-300">
				<caption className="sr-only">{ariaLabel}</caption>
				<thead className="sticky-table-header">
					{columnGroups !== undefined && <tr>
						{columnGroups.map((group, g) => <th key={`${group.label}-${g}`} scope="colgroup" colSpan={group.span}
							className={Utilities.classNames('whitespace-nowrap px-3 pt-2.5 pb-1 text-center text-xs font-semibold uppercase tracking-wide text-gray-500',
								g > 0 ? 'border-l border-gray-200' : '')}>
							{group.label}
						</th>)}
					</tr>}
					<tr>
						{columns.map((column, i) => {
							const isNumber = isNumberFormat(column.format ?? 'text');
							const active = sort !== undefined && sort.columnId === column.id;
							const ariaSort = active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none';
							const SortIcon = !active ? ChevronUpDownIcon : (sort.direction === 'asc' ? ChevronUpIcon : ChevronDownIcon);
							const key = keyColors[i];
							const label = <span className={Utilities.classNames('flex items-center gap-x-1.5', column.sortable === false && isNumber ? 'justify-end' : '')}>
								{key !== undefined && <span aria-hidden="true" className="inline-block h-0.5 w-3 shrink-0 rounded-full" style={{ background: key }} />}
								{column.label}
							</span>;
							return <th key={column.id} scope="col" aria-sort={column.sortable === false ? undefined : ariaSort}
								className={Utilities.classNames(
									'whitespace-nowrap py-2.5 text-xs font-semibold text-gray-900',
									i === 0 ? 'sticky left-0 z-10 bg-white pl-4 pr-3 sm:pl-6' : 'px-3',
									isNumber ? 'text-right' : 'text-left',
									groupStarts.has(i) ? 'border-l border-gray-200' : '',
								)}>
								{column.sortable === false
									? label
									: <button type="button" onClick={() => onSort(column)}
										className={Utilities.classNames(
											'group flex w-full items-center gap-x-1 rounded focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
											isNumber ? 'flex-row-reverse' : '',
										)}>
										{label}
										<SortIcon aria-hidden="true" className={Utilities.classNames('h-4 w-4 shrink-0', active ? 'text-gray-700' : 'text-gray-300 group-hover:text-gray-500')} />
									</button>}
							</th>;
						})}
					</tr>
				</thead>
				<tbody className="divide-y divide-gray-200">
					{sorted.length === 0
						? <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-gray-500 sm:px-6">{emptyLabel}</td></tr>
						: sorted.flatMap(row => {
							const hasChildren = (row.children ?? []).length > 0;
							const open = hasChildren && expanded.has(row.id);
							const lines = [renderRow(row, 'top', hasChildren, open)];
							if (open) (row.children ?? []).forEach(child => lines.push(renderRow(child, 'child', false, false)));
							return lines;
						})}
					{footer !== undefined && sorted.length > 0 && renderRow(footer, 'footer', false, false)}
				</tbody>
			</table>
		</div>
	</div>;
};

export default DataTable;
